import { useState, useMemo } from 'react';
import { 
  Search, Filter, ArrowUpDown, ChevronDown, ChevronRight, 
  Droplets, Gauge, Activity, AlertTriangle, Calendar, MapPin,
  Download, MoreHorizontal, ArrowUpRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { useProductionCalculator } from '../hooks/useProductionCalculator';

interface Well {
  id: string;
  name: string;
  apiNumber?: string;
  status: 'active' | 'inactive' | 'maintenance' | 'shut_in';
  oilProduction?: number;
  waterProduction?: number;
  gasProduction?: number;
  lastEntry?: string;
  assignedPumper?: string;
  nextCheck?: string;
  pressure?: {
    tubing?: number;
    casing?: number;
  };
  location?: {
    lat: number;
    lng: number;
  };
}

interface WellViewListingsProps {
  wells: Well[];
  onBack: () => void;
  onSelectWell: (well: Well) => void;
  initialFilter?: string; // 'active', 'overdue', 'top_producers', etc.
  initialSort?: { key: string; direction: 'asc' | 'desc' } | null;
  /** Optional calculated production values */
  calculatedProduction?: Map<string, {
    netOilBBL: number;
    grossWaterBBL: number;
    waterCutPercent: number;
  }>;
}

export default function WellViewListings({ 
  wells = [], 
  onBack, 
  onSelectWell,
  initialFilter = 'all',
  initialSort = null,
  calculatedProduction: externalCalcProduction
}: WellViewListingsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(initialSort);
  const [expandedWellId, setExpandedWellId] = useState<string | null>(null);

  // Production calculator - use utility functions for display calculations
  const { calculateWaterCutPercent } = useProductionCalculator();
  
  // Calculate production values internally when not provided externally
  // Uses simple tank-to-BBL conversions for display purposes
  const calculatedProduction = useMemo(() => {
    if (externalCalcProduction) return externalCalcProduction;
    
    const results = new Map<string, {
      netOilBBL: number;
      grossWaterBBL: number;
      waterCutPercent: number;
    }>();
    
    wells.forEach((well: any) => {
      try {
        const tankData = well.latestEntry?.tankGauges || well.tankGauges;
        if (tankData) {
          // Calculate oil from tank gauges
          let totalOilBBL = 0;
          let totalWaterInOilBBL = 0;
          (tankData.oil || []).forEach((t: any) => {
            const currentInches = t.topGauge || t.inches || 0;
            const previousInches = t.previousTopGauge || t.bottomGauge || 0;
            const bblPerInch = t.bblPerInch || 3.57;
            const levelChange = currentInches - previousInches;
            const barrelsProduced = levelChange * bblPerInch + (t.barrelsHauled || 0);
            totalOilBBL += Math.max(0, barrelsProduced);
            
            // Account for water in oil tanks (Kolor Kut)
            if (t.kolorKut?.waterCutInches > 0) {
              totalWaterInOilBBL += t.kolorKut.waterCutInches * bblPerInch;
            }
          });
          
          // Calculate water from water tanks
          let totalWaterBBL = 0;
          (tankData.water || []).forEach((t: any) => {
            const currentInches = t.topGauge || t.inches || 0;
            const previousInches = t.previousTopGauge || t.bottomGauge || 0;
            const bblPerInch = t.bblPerInch || 3.57;
            const levelChange = currentInches - previousInches;
            const barrelsProduced = levelChange * bblPerInch + (t.barrelsHauled || 0);
            totalWaterBBL += Math.max(0, barrelsProduced);
          });
          
          // Net oil excludes water detected in oil tanks
          const netOilBBL = Math.max(0, totalOilBBL - totalWaterInOilBBL);
          const grossWaterBBL = totalWaterBBL + totalWaterInOilBBL;
          const waterCutPercent = calculateWaterCutPercent(netOilBBL, grossWaterBBL);
          
          if (netOilBBL > 0 || grossWaterBBL > 0) {
            results.set(well.id, {
              netOilBBL: Math.round(netOilBBL * 100) / 100,
              grossWaterBBL: Math.round(grossWaterBBL * 100) / 100,
              waterCutPercent: Math.round(waterCutPercent * 10) / 10,
            });
          }
        }
      } catch (err) {
        // Fall back to raw values
      }
    });
    
    return results.size > 0 ? results : undefined;
  }, [wells, externalCalcProduction, calculateWaterCutPercent]);
  
  // Helper to get production values for a well
  const getWellProduction = (well: Well) => {
    const calc = calculatedProduction?.get(well.id);
    if (calc) {
      return { oil: calc.netOilBBL, water: calc.grossWaterBBL };
    }
    return { oil: well.oilProduction || 0, water: well.waterProduction || 0 };
  };

  // Filter Logic
  const filteredWells = useMemo(() => {
    let result = [...wells];

    // 1. Apply Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(w => 
        w.name.toLowerCase().includes(query) || 
        w.apiNumber?.toLowerCase().includes(query) ||
        w.assignedPumper?.toLowerCase().includes(query)
      );
    }

    // 2. Apply Category Filter
    switch (activeFilter) {
      case 'active':
        result = result.filter(w => w.status === 'active');
        break;
      case 'overdue':
        // Logic for overdue: lastEntry not today
        const today = new Date().toDateString();
        result = result.filter(w => !w.lastEntry || new Date(w.lastEntry).toDateString() !== today);
        break;
      case 'top_producers':
        // Top 20% by oil production (using calculated values)
        const sortedByOil = [...result].sort((a, b) => getWellProduction(b).oil - getWellProduction(a).oil);
        result = sortedByOil.slice(0, Math.ceil(result.length * 0.2));
        break;
      case 'maintenance':
        result = result.filter(w => w.status === 'maintenance' || w.status === 'shut_in');
        break;
      default:
        break;
    }

    // 3. Apply Sorting (use calculated values for production sorting)
    if (sortConfig) {
      result.sort((a: any, b: any) => {
        let aVal, bVal;
        
        // Use calculated production for production-related sorting
        if (sortConfig.key === 'oilProduction') {
          aVal = getWellProduction(a).oil;
          bVal = getWellProduction(b).oil;
        } else if (sortConfig.key === 'waterProduction') {
          aVal = getWellProduction(a).water;
          bVal = getWellProduction(b).water;
        } else {
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
        }
        
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [wells, searchQuery, activeFilter, sortConfig]);

  // Sort Handler
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Render Status Badge
  const StatusBadge = ({ status }: { status: string }) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      maintenance: 'bg-amber-100 text-amber-800 border-amber-200',
      shut_in: 'bg-red-100 text-red-800 border-red-200'
    };
    const label = status.replace('_', ' ').toUpperCase();
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status as keyof typeof styles] || styles.inactive}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Well Data Explorer</h1>
            <p className="text-sm text-gray-500">Manage and analyze {wells.length} wells</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={onBack} className="flex-1 sm:flex-none text-sm">Back</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white flex-1 sm:flex-none text-sm">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>
        </div>

        {/* Controls Bar */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text"
              placeholder="Search by name, API, or pumper..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
            {[
              { id: 'all', label: 'All Wells' },
              { id: 'active', label: 'Active' },
              { id: 'overdue', label: 'Overdue' },
              { id: 'top_producers', label: 'Top Producers' },
              { id: 'maintenance', label: 'Maintenance' }
            ].map(filter => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                  ${activeFilter === filter.id 
                    ? 'bg-blue-600 text-white shadow-md' 
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}
                `}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0">
            <div className="col-span-4 md:col-span-3 flex items-center cursor-pointer hover:text-blue-600" onClick={() => handleSort('name')}>
              Well Name <ArrowUpDown className="w-3 h-3 ml-1" />
            </div>
            <div className="col-span-2 hidden md:block cursor-pointer hover:text-blue-600" onClick={() => handleSort('status')}>
              Status <ArrowUpDown className="w-3 h-3 ml-1" />
            </div>
            <div className="col-span-3 md:col-span-2 text-right cursor-pointer hover:text-blue-600" onClick={() => handleSort('oilProduction')}>
              Oil (BPD) <ArrowUpDown className="w-3 h-3 ml-1" />
            </div>
            <div className="col-span-3 md:col-span-2 text-right cursor-pointer hover:text-blue-600" onClick={() => handleSort('waterProduction')}>
              Water (BPD) <ArrowUpDown className="w-3 h-3 ml-1" />
            </div>
            <div className="col-span-2 hidden md:block text-right cursor-pointer hover:text-blue-600" onClick={() => handleSort('lastEntry')}>
              Last Check <ArrowUpDown className="w-3 h-3 ml-1" />
            </div>
            <div className="col-span-2 md:col-span-1 text-center">Actions</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            {filteredWells.map((well) => (
              <div key={well.id} className="group">
                {/* Row Main */}
                <div 
                  className={`
                    grid grid-cols-12 gap-4 p-4 items-center hover:bg-blue-50/50 transition-colors cursor-pointer
                    ${expandedWellId === well.id ? 'bg-blue-50/30' : ''}
                  `}
                  onClick={() => setExpandedWellId(expandedWellId === well.id ? null : well.id)}
                >
                  <div className="col-span-4 md:col-span-3 flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${expandedWellId === well.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'}`}>
                      {expandedWellId === well.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{well.name}</div>
                      <div className="text-xs text-gray-500">{well.apiNumber || 'No API #'}</div>
                    </div>
                  </div>

                  <div className="col-span-2 hidden md:block">
                    <StatusBadge status={well.status} />
                  </div>

                  <div className="col-span-3 md:col-span-2 text-right font-mono text-gray-700">
                    {getWellProduction(well).oil.toFixed(1)}
                  </div>

                  <div className="col-span-3 md:col-span-2 text-right font-mono text-gray-700">
                    {getWellProduction(well).water.toFixed(1)}
                  </div>

                  <div className="col-span-2 hidden md:block text-right text-sm text-gray-600">
                    {well.lastEntry ? new Date(well.lastEntry).toLocaleDateString() : 'Never'}
                  </div>

                  <div className="col-span-2 md:col-span-1 flex justify-center">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectWell(well);
                      }}
                    >
                      <ArrowUpRight className="w-4 h-4 text-blue-600" />
                    </Button>
                  </div>
                </div>

                {/* Expanded Detail View */}
                <AnimatePresence>
                  {expandedWellId === well.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden bg-gray-50 border-b border-gray-100"
                    >
                      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Column 1: Key Stats */}
                        <div className="space-y-4">
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Performance Metrics</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <div className="text-xs text-gray-500 mb-1">Gas Production</div>
                              <div className="text-lg font-bold text-gray-900">{well.gasProduction || 0} <span className="text-xs font-normal text-gray-400">MCF</span></div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <div className="text-xs text-gray-500 mb-1">Tubing Pressure</div>
                              <div className="text-lg font-bold text-gray-900">{well.pressure?.tubing || 0} <span className="text-xs font-normal text-gray-400">PSI</span></div>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-gray-200">
                              <div className="text-xs text-gray-500 mb-1">Casing Pressure</div>
                              <div className="text-lg font-bold text-gray-900">{well.pressure?.casing || 0} <span className="text-xs font-normal text-gray-400">PSI</span></div>
                            </div>
                          </div>
                        </div>

                        {/* Column 2: Operational Info */}
                        <div className="space-y-4">
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Operational Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between py-2 border-b border-gray-200">
                              <span className="text-gray-500">Assigned Pumper</span>
                              <span className="font-medium text-gray-900">{well.assignedPumper || 'Unassigned'}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-gray-200">
                              <span className="text-gray-500">Next Scheduled Check</span>
                              <span className="font-medium text-gray-900">{well.nextCheck || 'Not Scheduled'}</span>
                            </div>
                            <div className="flex justify-between py-2 border-b border-gray-200">
                              <span className="text-gray-500">Location</span>
                              <span className="font-medium text-gray-900 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> 
                                {well.location?.lat != null && well.location?.lng != null 
                                  ? `${well.location.lat.toFixed(4)}, ${well.location.lng.toFixed(4)}` 
                                  : 'No GPS'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Column 3: Actions */}
                        <div className="flex flex-col justify-end gap-3">
                          <Button 
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => onSelectWell(well)}
                          >
                            Open Full Well Dashboard
                          </Button>
                          <Button variant="outline" className="w-full">
                            View History Log
                          </Button>
                          <Button variant="outline" className="w-full text-red-600 hover:bg-red-50 border-red-200">
                            Report Issue
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
            
            {filteredWells.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900">No wells found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
